// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.HighchartUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  get,
  isNil,
  size,
  head,
  find,
  includes,
  isFunction,
  isArray,
  flow,
  trimCharsStart,
  has,
  isDate,
  uniq,
  each,
  curry,
  getOr,
  isUndefined,
  isEmpty,
  isEqual,
  escape,
  reverse,
  forEach,
  tail,
  some,
  startsWith
} from 'lodash/fp';

import { UNDEFINED_VALUE } from 'logic/data/Izenda.UIConstants';
import { RANGE_ALIAS } from 'logic/data/Izenda.ReportPartChartConstants';
import constants from 'logic/data/Izenda.ReportPartChartConstants';
import ChartDataParser, { getCellColor } from 'share/utils/Izenda.ChartDataParser';
import { getFieldName } from 'share/utils/Izenda.ProcessDataUtils';
import AppGlobal from 'Izenda.AppGlobal';
import { DefaultConfig } from 'logic/data/Izenda.ChartDefaultConfig';
import convertToDateTimeFormat from 'share/utils/Izenda.ConvertToDateTimeFormat';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { MappingValues } from 'logic/data/Izenda.ChartDefaultConfig';
import jsFormatService from 'share/Izenda.JsFormatService';
import Highcharts from 'highcharts';

const { STACK_TYPE_CHART_VALUES } = MappingValues;

/**
 * Add point action link
 * @param {boolean} isMainAxis
 * @param {Object} chartOptions
 * @param {Object} configSource
 * @param {string} fieldName
 * @param {Object} fieldConfig
 * @param {Function} fnGetPointName
 */
function addPointActionLink(
  isMainAxis: boolean,
  chartOptions: any,
  configSource: any,
  fieldName: string,
  fieldConfig: any,
  fnGetPointName: () => mixed,
  isMultiLevel: boolean = false,
  rotation: number
): any {
  if (!get('numberOfPointActions', fieldConfig)) {
    return configSource;
  }

  const oldFormatter = get('formatter', configSource);
  const pointAction = get('commonActions.pointAction', chartOptions);
  const chartContainer = get('commonActions.chartContainer', chartOptions);
  const query = `${isMainAxis ? '.is-mainAxis' : '.is-subAxis'}`;
  const classData = {
    name: `hyperlink-action u_textLink ${isMainAxis ? 'is-mainAxis' : 'is-subAxis'}`,
    query: isMultiLevel ? `${query}.${fieldName}` : query
  };

  chartContainer && $izenda(chartContainer).off('click', classData.query);
  chartContainer && $izenda(chartContainer).on('click', classData.query, function(e) {
    e.stopPropagation();
    const $link = $izenda(e.currentTarget);
    let linkData;
    if ($link[0].nodeName.toUpperCase() === 'TSPAN') {
      // when label rotates, the highcharts create tspan instead of label formatter function result
      linkData = $link.parent().data();
    } else {
      linkData = $link.data();
    }
    const filters = JSON.parse(decodeURI(linkData.filters));
    const separator = JSON.parse(decodeURI(linkData.separator));

    pointAction(
      fieldConfig,
      [
        {
          key: filters[0],
          value: filters[1]
        }
      ],
      separator.separatorColumnName && separator
    );
  });

  const config = $izenda.extend(configSource, {
    useHTML: true,
    hasPointAction: true,
    className: classData.name,
    getData: getDataByPoint,
    formatter: function() {
      const text = oldFormatter.apply(this, arguments);

      // incase axix type is linear or datetime, only render link for value which have point link to it
      if (!isNil(this.axis) && !size(this.axis.categories) && !isPointValue(this.axis, this.chart.series, this.value)) {
        return text;
      }

      if (isMultiLevel
        && fieldName !== get('point.labelName', this)
        && fieldName !== get('point.series.userOptions.labelName', this)) {
        return text;
      }

      const data = getDataByPoint.call(this, text);
      const classname = isMultiLevel ? `${classData.name} ${data.labelName}` : classData.name;
      const dataFilters = data['data-filters'];
      const dataSeparator = data['data-separator'];

      if (!isNil(rotation)) {
        // rotate data labels manually, because default highcharts rotation cancels
        // the "formatter: () => {...}" result and adds 2 <tspan> tags instead of it.
        // the second hidden span is needed for correct positioning.
        const rotationStyle = `position: absolute; transform: rotate(${rotation || 0}deg)`;
        const result1 = `<a class="${classname}" style="${rotationStyle}" data-filters="${dataFilters}" data-separator="${dataSeparator}">${text}</a>`;
        const result2 = `<span class="${classname}" style="visibility: hidden;">${text}</span>`;
        return result1 + result2;
      } else {
        // the default link (rotation applied in highcharts configuration)
        return `<a class="${classname}" data-filters="${dataFilters}" data-separator="${dataSeparator}">${text}</a>`;
      }


      function isPointValue(axis: any, series: Array<any>, value: any): boolean {
        let result = false;
        if (isNil(series) || isEmpty(series)) {
          return result;
        }

        const xSerieData = get('processedXData', head(series));
        if (isNil(xSerieData)) {
          return result;
        }

        if (axis.isDatetimeAxis) {
          const valueInDate = axis.isDatetimeAxis ? new Date(value).getTime() : null;
          result = !!find(item => valueInDate === new Date(item).getTime(), xSerieData);
        } else {
          result = includes(value, xSerieData);
        }

        return result || isPointValue(axis, tail(series), value);
      }
    }
  });
  isMultiLevel && (delete config.className);
  return config;

  function getDataByPoint(displayText) {
    const userOption = get('series.userOptions', this);
    const separatorColumnName = get('separatorField.columnName', userOption);
    const separatorValue = get('separatorValue', userOption);
    let pointName = isFunction(fnGetPointName) ? fnGetPointName.call(this) : this.value || this.key || this.x;
    const isDateTypeType = get('nameField.reportPartElm.isDateTimeType', userOption);
    const fieldFormatData = get('nameField.reportPartElm.fieldFormatData', userOption);

    pointName = this.dateTimeLabelFormat
      ? displayText
      : isDateTypeType
      ? convertToDateTimeFormat(fieldFormatData, pointName)
      : pointName;

    const config = find(i => i.name === pointName, get('data', userOption));
    const fieldNameValue = isMultiLevel ? config.labelName : fieldName;
    let data = {
      'data-filters': encodeURI(JSON.stringify([fieldNameValue, pointName])),
      'data-separator': encodeURI(
        JSON.stringify({
          separatorColumnName: separatorColumnName,
          separatorValue: separatorValue
        })
      )
    };

    if (isMultiLevel) {
      if (get('nameField.fieldNameAlias', config) !== fieldName) {
        $izenda.extend(data, {
          class: `${config.labelName}`,
          labelName: config.labelName,
          isKeepDataInMultiLevel:true
        });
      } else {
        $izenda.extend(data, {
          class: `${classData.name} ${config.labelName}`,
          labelName: config.labelName
        });
      }
    }

    return data;
  }
}

/**
 * set default axis name
 * @param {Object} chartOptions
 * @param {Object} chartData
 */
function setDefaultAxisName(chartOptions: any, chartData: any): void {
  try {
    const containers = chartData.containers;
    const expandedLevel = chartData.expandedLevel;
    const labelsElements = containers.labels;
    const xAxis = isArray(chartOptions.xAxis) ? chartOptions.xAxis[expandedLevel] : chartOptions.xAxis;
    const labelsFieldName = labelsElements[expandedLevel] && labelsElements[expandedLevel].fieldName;
    const xTitle = {
      text: labelsFieldName
    };
    !xAxis.title && (xAxis.title = xTitle);

    const valuesElements = containers.values;
    const yAxis = chartOptions.yAxis;
    const valuesEleLength = valuesElements && valuesElements.length;
    const numberOne = 1;

    let yTitle;
    if (valuesEleLength > numberOne) {
      yTitle = {
        text: DefaultConfig.METRIC_ALL
      };
      !yAxis.title && (yAxis.title = yTitle);
    } else if (valuesEleLength === numberOne) {
      yTitle = {
        text: removeMinMaxAlias(valuesElements[0].fieldName)
      };
      !yAxis.title && (yAxis.title = yTitle);
    }
  } catch (e) {
    // do nothing
  }

  // Remove Min/Max alias if it exits in field name alias
  function removeMinMaxAlias(name: string): any {
    if (chartOptions.izendaOptions && !isNil(chartOptions.izendaOptions.range)) {
      return flow(
        trimCharsStart(RANGE_ALIAS.MIN_ALIAS),
        trimCharsStart(RANGE_ALIAS.MAX_ALIAS),
        trimCharsStart(RANGE_ALIAS.AVG_ALIAS)
      )(name);
    }
    return name;
  }
}

/**
 * get point name
 * @param {Object} point
 */
function getPointName(point: any): string {
  const name = getPointData('name', point);

  if (!isNil(name)) {
    return name;
  }

  if (has('category', point) && !isDate(point.category)) {
    return getPointData('x', point) || getPointData('category', point);
  }

  const key = getPointData('key', point);
  const x = getPointData('x', point);

  if (!isNil(key)) {
    return key;
  } else {
    return x;
  }
}

/**
 * Get point data
 * @param {string} key
 * @param {Object} point
 */
function getPointData(key: string, point: any): any {
  const rawKey = key + 'RawData';

  if (has(rawKey, point)) {
    return get(rawKey, point);
  }

  if (has(key, point)) {
    return get(key, point);
  }

  return;
}

/**
 * get point value
 * @param {Object} point
 */
function getPointValue(point: any): any {
  const val = getPointData('y', point);

  if (!isNil(val)) {
    return val;
  }

  return getPointData('value', point);
}

/**
 * get point options
 * @param {Object} point
 */
function getPointOptions(point: any): any {
  const highchartPoint = get('point', point) || point;
  return highchartPoint && highchartPoint.options;
}

/**
 * get axis type
 * @param {Object}  field
 * @param {Array<any>} axisData
 * @param {Object} axisOption
 */
function getAxisType(field: any, axisData: Array<any>, axisOption: any): string {
  const dataType = get('reportPartElm.previewDataType', field);
  const izCategory = axisOption ? !!axisOption.izCategory : false;

  let type, isCategory;
  switch (dataType) {
    case 'Money':
    case 'Numeric':
      type = 'linear';
      isCategory = izCategory;
      break;
    case 'Datetime':
    case 'Time':
      type = 'datetime';
      isCategory = izCategory;
      break;
    default:
      isCategory = true;
      type = 'category';
  }

  return {
    type: type,
    categories: isCategory ? uniq(axisData) : undefined
  };
}

/**
 * get color for a serie
 * @param {Object} chartOptions
 * @param {Object} serieConfig
 * @param {Object} serie
 * @return {Object}
 */
function getColorForSiries(chartOptions: any, serieConfig: any, serie: any): any {
  const serieData = get('data', serie);
  if (isNil(serie) || isNil(serieData) || isNil(serieConfig)) {
    return;
  }

  let sameRange = true;
  let color;
  let firstGetColor = true;
  const yConfig = getSerieConfig(serie.yAxisField, serieConfig);

  for (let i = 0; i < serieData.length; ++i) {
    const value = getY(chartOptions, serieData[i], i);
    const cellColor = get('cellColor', yConfig);
    const record = serieData[i].record;
    if (!isNil(value)) {
      const colorInRange = ReportPartUtils.getConfigDataByRange(value, cellColor, {
        element: serie.yAxisField,
        record: record
      });

      if (firstGetColor) {
        firstGetColor = false;
        color = colorInRange;
      } else if (color != colorInRange) {
        sameRange = false;
        break;
      }
    }
  }
  return sameRange ? color : null;
}

/**
 * set color for series
 * @param {Object} userOptions: options and values
 * @param {Object} series: series data for chart
 * @param {Object} colors: array of color
 * @return {Object}
 */
function setColorPointForAreaLine(userOptions: any, series: any, colors: any) {
  if (userOptions.izMultiColor && !userOptions.izDisableMultiColor) {
    forEach(serie => {
      let colorIndex = 0;
      forEach(point => {
        let pointColor = !isEmpty(colors) ? colors[colorIndex] : null;
        if (!isEmpty(pointColor) && isEmpty(point.color)) {
          point.color = pointColor;
        }
        colorIndex++;
        if (colorIndex > colors.length - 1) {
          colorIndex = 0;
        }
      })(serie.data);
    })(series);
  }
}

/**
 * get color of point for serie
 * @param {Object} chartOptions
 * @param {Object} serieConfig
 * @param {Object} serie
 */
function getColorPointBySerie(chartOptions: any, serieConfig: any, serie: any): any {
  const serieData = get('data', serie);
  if (isNil(serie) || isNil(serieData) || isNil(serieConfig)) {
    return;
  }

  const separatorColumnName = get('separatorField.columnName', serie);
  const separatorRecord = !isNil(serie.separatorField) ? flow(
    find((item) => {
      return !isNil(item.record) && has(separatorColumnName, item.record);
    }),
    get('record')
  )(serieData) : null;

  // color by serie
  const separatorOriginalValue = !isNil(serie.separatorField)
    ? ReportPartUtils.getOriginalValue(serie.separatorField, separatorRecord)
    : null;

  const separatorConfig = getSerieConfig(serie.separatorField, serieConfig);
  const serieColor =
  !isNil(separatorOriginalValue) &&
  !isNil(separatorConfig) &&
    getCellColor(separatorOriginalValue, separatorConfig, {
      element: serie.separatorField,
      record: separatorRecord
    });
  serieColor && (serie.color = serieColor);
  // color by point
  const xConfigMaster = getSerieConfig(serie.xAxisField, serieConfig);
  const yConfigMaster = getSerieConfig(serie.yAxisField, serieConfig);
  const zConfigMaster = getSerieConfig(serie.zAxisField, serieConfig);
  const nameConfigMaster = getSerieConfig(serie.nameField, serieConfig);
  const valueConfigMaster = getSerieConfig(serie.valueField, serieConfig);
  const sizeOfData = size(serieData);

  if (hasFamilyRelationship(serieData)) {
    const populateColor = (index, color) => {
      color = color || getColor(serieData[index], index);
      if (color) {
        serieData[index].color = color;
        for (let i = 0; i < sizeOfData; i++) {
          if (
            serieData[i].parent &&
            serieData[i].parent === serieData[index].id &&
            isUndefined(getColor(serieData[i], i))
          ) {
            populateColor(i, color);
          }
        }
      }
    };

    for (let i = 0; i < sizeOfData; i++) {
      populateColor(i);
    }
  } else {
    // color for separator siries
    let index = sizeOfData;
    while (index--) {
      const color = getColor(serieData[index], index);
      color && (serieData[index].color = color);
    }
  }

  // color for legend
  const color = getColorForSiries(chartOptions, serieConfig, serie);
  color && (serie.color = color);

  function getColor(point, pointIndex) {
    const record = point.record;
    const value = !isNil(serie.valueField)
      ? ReportPartUtils.getOriginalValue(serie.valueField, record)
      : getPointData('value', point);
    let xConfig = xConfigMaster;
    let yConfig = yConfigMaster;
    let zConfig = zConfigMaster;
    let nameConfig = nameConfigMaster;
    let valueConfig = valueConfigMaster;

    !xConfig && (xConfig = getSerieConfig(point.xAxisField, serieConfig));
    !yConfigMaster && (yConfig = getSerieConfig(point.yAxisField, serieConfig));
    !zConfigMaster && (zConfig = getSerieConfig(point.zAxisField, serieConfig));
    !nameConfigMaster && (nameConfig = getSerieConfig(point.nameField, serieConfig));
    !valueConfigMaster && (valueConfig = getSerieConfig(point.valueField, serieConfig));

    return (
      getCellColor(value, valueConfig, {
        element: serie.valueField,
        record: record
      }) ||
      getCellColor(getPointData('z', point), zConfig, {
        element: serie.zAxisField,
        record: record
      }) ||
      getCellColor(getY(chartOptions, point, pointIndex), yConfig, {
        element: serie.yAxisField,
        record: record
      }) ||
      getCellColor(getPointData('originalNameValue', point) || getPointData('name', point), nameConfig, {
        element: serie.nameField,
        record: record
      }) ||
      getCellColor(getX(chartOptions, point, pointIndex), xConfig, {
        element: serie.xAxisField,
        record: record
      })
    );
  }
}

/**
 * get color of point for serie
 * @param {Object} series
 * @param {Object} chartOptions
 * @param {Object} serieConfig
 */
function getColorPointBySeries(series: any, chartOptions: any, serieConfig: any): any {
  each(curry(getColorPointBySerie)(chartOptions, serieConfig), series);
}

/**
 * get serie config
 * @param  {Object} field
 * @param  {Object} seriesConfig
 * @return {Object}
 */
function getSerieConfig(field: any, seriesConfig: any): any {
  return seriesConfig[getFieldName(field)];
}

/**
 * Get value of X
 * @param  {Object} chartOptions
 * @param  {Object} point
 * @param  {number} pointIndex
 * @return {Object}
 */
function getX(chartOptions: any, point: any, pointIndex: number): any {
  const xCategories = get('xAxis.categories', chartOptions);
  let xValue = point.xRawData;

  if (!isNil(xValue)) {
    return xValue;
  }

  if (isArray(xCategories)) {
    xValue = getPointData('key', point);
    if (!isNil(xValue)) {
      return xValue;
    }
    xValue = getPointData('x', point);
    return isNil(pointIndex) ? xCategories[xValue] : xCategories[xValue] || xCategories[pointIndex];
  }

  return getPointData('x', point) || getPointData('key', point) || getPointData('name', point);
}

/**
 * Get value of Y
 * @param  {Object} chartOptions
 * @param  {Object} point
 * @param  {number} pointIndex
 * @return {Object}
 */
function getY(chartOptions: any, point: any, pointIndex: number): any {
  const yCategories = get('yAxis.categories', chartOptions);
  let yValue = point.yRawData;

  if (!isNil(yValue)) {
    return yValue;
  }

  yValue = getPointData('y', point);

  if (isArray(yCategories)) {
    return isNil(pointIndex) ? yCategories[yValue] : yCategories[yValue] || yCategories[pointIndex];
  }

  const chartPoint = has('point', point) ? point.point : point;

  // Get percentage value instead raw value
  const isStackedPercentage = get('plotOptions.series.stacking', chartOptions) === STACK_TYPE_CHART_VALUES.izPercent;

  if (isStackedPercentage && has('percentage', chartPoint)) {
    yValue = get('percentage', chartPoint);
  }

  return yValue;
}

/**
 * Get formatted value of axis
 * @param  {Object} axis
 * @param  {Object} chartOptions
 * @return {string}
 */
function getFormattedLabelAxis(axis: any, chartOptions: any): any {
  axis = axis || this.axis || {};
  chartOptions = chartOptions || getOr({}, 'chart.userOptions', this);

  const axisFieldName = `${axis.coll || 'xAxis'}Field`;
  const axisField = flow(
    head,
    get(axisFieldName)
  )(chartOptions.series);
  const axisCategories = axis.categories;
  let value = this.value;
  let formattedValue;

  if (size(axisCategories)) {
    value = includes(value, axisCategories) ? value : null;
  } else if (axis.isDatetimeAxis) {
    value = new Date(value);
  }

  formattedValue = formatterData(value, axisField, getSerieConfig(axisField, chartOptions.izendaSeriesConfig));
  return showText(formattedValue && formattedValue.data);
}

/**
 * Read data of point
 * @param  {string}  key
 * @param  {Object}  point
 * @param  {boolean} isFormatted TRUE: default
 * @return {Object}
 */
function getFormattedDataByPoint(key: string, point: any, isFormatted: boolean): any {
  const series = getOr({}, 'series', point);
  const serieOpt = get('userOptions', series);
  const chartOptions = getOr({}, 'chart.userOptions', series);
  const serieConfig = chartOptions.izendaSeriesConfig;
  let result, fieldConfig;

  switch (key) {
    case 'x':
      result = getX(chartOptions, point);
      fieldConfig = serieOpt.xAxisField || point.xAxisField;
      break;
    case 'y':
      result = getY(chartOptions, point);
      fieldConfig = serieOpt.yAxisField || point.yAxisField;
      break;
    case 'z':
      result = getPointData('z', point);
      fieldConfig = serieOpt.zAxisField || point.zAxisField;
      break;
    case 'name':
      result = getPointData('name', point);
      fieldConfig = serieOpt.nameField || point.nameField;
      break;
    case 'value':
      result = getPointData('value', point);
      fieldConfig = serieOpt.valueField || point.valueField;
      break;
    default:
      result = point[key];
      fieldConfig = serieOpt[key + 'Field'];
  }

  return formatterData(
    result,
    fieldConfig,
    getSerieConfig(fieldConfig, serieConfig),
    {
      element: fieldConfig,
      record: get('options.record', has('point', point) ? point.point : point),
      nameFieldValue: {
        value: point.name,
        originalValue: point.originalNameValue
      }
    },
    isFormatted,
    getOr(false, 'below', point)
  );
}

/**
 * Format data
 * @param {Object} value
 * @param {Object} valueField
 * @param {Object} serieConfig
 * @param {Object} fieldSource
 * @param {boolean} isFormatted
 */
function formatterData(value: any, valueField: any, serieConfig: any, fieldSource: any, isFormatted: boolean, isBelow: boolean = false): any {
  isFormatted = isNil(isFormatted) ? true : isFormatted;
  const result = {
    fieldName: getFieldName(valueField)
  };
  const record = get('record', fieldSource);

  let columnName = get('columnName', valueField);
  const jsFormatString = get('reportPartElm.properties.dataFormattings.format.jsFormatString', valueField);

  let formattedValue;
  if (jsFormatString) {
    const dataType = get('reportPartElm.properties.dataFormattings.functionInfo.formatDataType', valueField);
    const jsFormatId = get('reportPartElm.properties.dataFormattings.format.formatId', valueField);
    formattedValue = formatDataByStringFormat(jsFormatId, value, dataType);
  }

  let valueStyle;
  if (!isEmpty(record) && columnName) {
    let fieldValues;
    if (has(columnName, record)) {
      if (isBelow && startsWith(RANGE_ALIAS.MAX_ALIAS, columnName)) {
        columnName = columnName.replace(RANGE_ALIAS.MAX_ALIAS, RANGE_ALIAS.MIN_ALIAS);
      }
      fieldValues = ReportPartUtils.getFieldValue(columnName, record);
    } else {
      const originalValue =  getOr(value,'nameFieldValue.originalValue', fieldSource);
      fieldValues = {
        value,
        originalValue
      };
    }

    if (jsFormatString) {
      fieldValues.value = formattedValue;
    }

    valueStyle = isFormatted && ChartDataParser.getValueStyle(fieldValues, serieConfig, fieldSource);
    result.data = getOr(value, 'fomattedValue', valueStyle);
  } else {
    valueStyle =
      isFormatted &&
      ChartDataParser.getValueStyle(
        {
          value: value
        },
        serieConfig,
        fieldSource
      );

    result.data = jsFormatString ? formattedValue : getOr(value, 'valueWithoutAltText', valueStyle);
  }

  return result;
}

/**
 * Show text
 * @param {Object} value
 */
function showText(value: any): string {
  const stringValue = String.commonStringValue(value, AppGlobal.advancedSettings.other.convertNullToEmptyStringValue);
  return escape(stringValue);
}

/**
 * Check if point is in a regression series
 * @param {Point} point
 */
function isRegressionLineSerie(point) {
  return get('series.userOptions.isRegressionLine', point);
}

/**
 * Return tooltip for each series
 * This function is used for all chart-type
 * @param {boolean} needReverse
 * @return {string}
 */
function getTooltipFormatter(needReverse: boolean): any {
  const tooltip = [];
  let points = this.points || [this.point];
  needReverse && (points = reverse(points));

  if (points.length === 1 && !isRegressionLineSerie(head(points))) {
    showAllInfo(head(points));
  } else {
    // all info should have the same name (x-axis value)
    const firstPoint = points[0] || {};
    const x = getFormattedDataByPoint('x', firstPoint);

    tooltip.push(`<b>${showText(x.data)}</b>`);
    points.forEach(showMainInfo);
  }

  return tooltip.join('<br>');

  function pointColorStyle(point) {
    return `<span style="color:${point.color || point.series.color}">●</span>`;
  }

  function pointResultYValue(point: any): string {
    const y = getFormattedDataByPoint('y', point);
    const yRawData = getRawYData(point);
    const userOptions = get('series.userOptions', point);
    const isSeparatorUsed = userOptions && userOptions.separatorField;
    const seriesName = isSeparatorUsed ? get('series.userOptions.name', point) : null;

    let result = `<span>${showText(!isNil(seriesName) ? seriesName : y.fieldName)}: \
      ${showText(yRawData.useYRawData ? yRawData.yRawData : y.data)}`;

    const series = getOr({}, 'series', point);
    const chartOptions = getOr({}, 'chart.userOptions', series);
    const isStackedPercentage = get('plotOptions.series.stacking', chartOptions) === STACK_TYPE_CHART_VALUES.izPercent;

    if (isStackedPercentage && has('percentage', point)) {
      const percentage = get('percentage', point);
      result += Highcharts.format(' ({percentage:.2f}%)', { percentage });
    }

    result += '</span>';
    return result;
  }

  function showMainInfo(point) {
    const isDefaultValue = get('point.options.isDefaultValue', point);

    !isDefaultValue &&
      tooltip.push(`${pointColorStyle(point)} ${pointResultYValue(point)}`);
  }

  function getRawYData(point) {
    let yRawData = get('point.options.yRawData', point);
    const useYRawData = !isUndefined(yRawData);
    yRawData = String.commonStringValue(yRawData, AppGlobal.advancedSettings.other.convertNullToEmptyStringValue);
    return {
      useYRawData: useYRawData,
      yRawData: yRawData
    };
  }

  function showAllInfo(point) {
    const name = getFormattedDataByPoint('name', point);
    const value = getFormattedDataByPoint('value', point);
    const x = getFormattedDataByPoint('x', point);
    const y = getFormattedDataByPoint('y', point);
    const z = getFormattedDataByPoint('z', point);
    const pointOptions = getPointOptions(point);

    if (pointOptions.isSum) {
      y.fieldName && tooltip.push(`<span>${showText(pointOptions.sumTitle)} <b>${showText(y.data)}</b></span>`);
      return;
    }

    name.fieldName &&
      tooltip.push(`${pointColorStyle(point)} <b>${showText(name.fieldName)}: ${showText(name.data)}</b>`);
    value.fieldName &&
      tooltip.push(`${pointColorStyle(point)} <b>${showText(value.fieldName)}: ${showText(value.data)}</b>`);

    if (!name.fieldName && !value.fieldName) {
      x.fieldName && tooltip.push(`${pointColorStyle(point)} <b>${showText(x.data)}</b>`);
    } else {
      x.fieldName && tooltip.push(`<span>${showText(x.fieldName)}: ${showText(x.data)}</span>`);
    }

    y.fieldName &&
      tooltip.push(`${pointResultYValue(point)}`);
    z.fieldName && tooltip.push(`<span>${showText(z.fieldName)}: ${showText(z.data)}</span>`);
  }
}

/**
 * Action drill down on gauge
 * @param {Object} userOptions
 * @param {Array} serie
 */
function onDrilldownGauge(userOptions: any, serie: Array<any>): void {
  return function() {
    const drilldownAction = get('commonActions.onDrilldown', userOptions);
    const pointName = get('data[0].name', serie);

    if (!isFunction(drilldownAction)) {
      return;
    }

    drilldownAction(
      {
        field: serie.xAxisField,
        value: getVal(pointName),
        record: get('data[0].record', serie)
      },
      {
        field: serie.separatorField,
        value: getVal(serie.separatorValue)
      }
    );
  };

  function getVal(val) {
    return val === UNDEFINED_VALUE ? '' : val;
  }
}

/**
 * Get custom format
 * @param {Object} userOptions
 * @returns {string}
 */
function getCustomFormat(userOptions: any) {
  const record = get('series[0].data[0].record', userOptions);
  const yAxisField = flow(
    get('series'),
    head,
    get('yAxisField')
  )(userOptions);

  const yAxisColumnName = flow(
    get('series'),
    head,
    get('yAxisField.columnName')
  )(userOptions);
  const jsFormatString = get('reportPartElm.properties.dataFormattings.format.jsFormatString', yAxisField);

  if (jsFormatString) {
    const dataType = get('reportPartElm.properties.dataFormattings.functionInfo.formatDataType', yAxisField);
    const jsFormatId = get('reportPartElm.properties.dataFormattings.format.formatId', yAxisField);
    return formatDataByStringFormat(jsFormatId, get(yAxisColumnName, record), dataType);
  }

  const hasCustomFormat = has(constants.CUSTOM_FORMAT_PREFIX + yAxisColumnName, record);

  return hasCustomFormat ? get(yAxisColumnName, record) : null;
}

/**
 * format data by string format
 * @param  {string} stringFormat java script string format
 * @param  {any} data         data to format
 * @return {string}              data after format
 */
function formatDataByStringFormat(stringFormat: string, value: any, dataType: string): any {
  let data;
  if (isNil(value) || isEqual(value, '')) {
    data = null;
  } else {
    data = dataType === 'Datetime' ? new Date(value) : value;
  }

  return jsFormatService.format(stringFormat, data, value);
}

function getFormatedValueByStringFormat(reportPartElm, value, fieldDataType) {
  let formatedValue;
  const jsFormatString = get(
    'properties.dataFormattings.format.jsFormatString',
    reportPartElm
  );
  const dataType = !isNil(fieldDataType) ? fieldDataType : get('field.dataFieldType', reportPartElm);

  if (jsFormatString) {
    const jsFormatId = get('properties.dataFormattings.format.formatId', reportPartElm);
    formatedValue = formatDataByStringFormat(jsFormatId, value, dataType);
  }

  return formatedValue;
}

/**
 * Whether serie has parent relationship
 * @param {Array<Object>} serie
 */
function hasFamilyRelationship(serie: Array<any>) {
  return some(i => !isNil(i.parent), serie);
}

export default {
  setDefaultAxisName,
  addPointActionLink,
  getPointName,
  getPointValue,
  formatterData,
  getFormatedValueByStringFormat,
  getPointOptions,
  getAxisType,
  getColorPointBySerie,
  getColorPointBySeries,
  getColorForSiries,
  getFormattedLabelAxis,
  getFormattedDataByPoint,
  getTooltipFormatter,
  onDrilldownGauge,
  showText,
  getCustomFormat,
  formatDataByStringFormat,
  setColorPointForAreaLine
};



// WEBPACK FOOTER //
// ./ui/share/components/charting/highcharts/Izenda.HighchartUtils.js